/**
 * Public Job Board - Embeddable via iframe
 * White-label careers portal for company websites
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import * as recruitmentService from '../../services/recruitmentService';

interface PublicJobBoardProps {
  companyId?: string;
  brandColor?: string;
  logoUrl?: string;
  companyName?: string;
  embedded?: boolean;
}

const PublicJobBoard: React.FC<PublicJobBoardProps> = ({
  companyId,
  brandColor = '#6366f1',
  logoUrl,
  companyName = 'Our Company',
  embedded = false
}) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Get params from URL or props
  const orgId = companyId || searchParams.get('company') || '';
  const color = searchParams.get('color') || brandColor;
  const logo = searchParams.get('logo') || logoUrl;
  const name = searchParams.get('name') || companyName;
  const isEmbedded = searchParams.get('embedded') === 'true' || embedded;
  
  const [jobs, setJobs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedLocation, setSelectedLocation] = useState('all');
  
  // Prevent multiple simultaneous loads
  const loadingRef = useRef(false);
  const loadedRef = useRef(false);

  useEffect(() => {
    // Only load once when component mounts
    if (!loadedRef.current && !loadingRef.current) {
      loadJobs();
    }
  }, []); // Empty dependency array - load only once

  const loadJobs = async () => {
    if (loadingRef.current) return; // Prevent concurrent requests
    
    try {
      loadingRef.current = true;
      setLoading(true);
      const allJobs = await recruitmentService.getJobs();
      // Filter to only active jobs posted to career site
      const publicJobs = allJobs.filter((job: any) => 
        job.status === 'active' && job.posted_to_career_site === true
      );
      setJobs(publicJobs);
      loadedRef.current = true;
    } catch (error: any) {
      console.error('Failed to load jobs:', error);
      // Set empty jobs array - will show "no vacancies" message
      setJobs([]);
      loadedRef.current = true;
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  };

  // Memoize filter options to prevent recalculation on every render
  const departments = useMemo(() => 
    ['all', ...Array.from(new Set(jobs.map(j => j.department).filter(Boolean)))],
    [jobs]
  );
  
  const types = useMemo(() => 
    ['all', ...Array.from(new Set(jobs.map(j => j.employment_type).filter(Boolean)))],
    [jobs]
  );
  
  const locations = useMemo(() => 
    ['all', ...Array.from(new Set(jobs.map(j => j.location).filter(Boolean)))],
    [jobs]
  );

  // Memoize filtered jobs to prevent recalculation on every render
  const filteredJobs = useMemo(() => {
    return jobs.filter(job => {
      const matchesSearch = !searchTerm || 
        job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.description?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesDepartment = selectedDepartment === 'all' || job.department === selectedDepartment;
      const matchesType = selectedType === 'all' || job.employment_type === selectedType;
      const matchesLocation = selectedLocation === 'all' || job.location === selectedLocation;
      
      return matchesSearch && matchesDepartment && matchesType && matchesLocation;
    });
  }, [jobs, searchTerm, selectedDepartment, selectedType, selectedLocation]);

  const handleJobClick = (jobId: string) => {
    if (isEmbedded) {
      // Open in new window for embedded version
      window.open(`/careers/jobs/${jobId}?company=${orgId}&color=${encodeURIComponent(color)}&name=${encodeURIComponent(name)}`, '_blank');
    } else {
      navigate(`/careers/jobs/${jobId}?company=${orgId}&color=${encodeURIComponent(color)}&name=${encodeURIComponent(name)}`);
    }
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isEmbedded ? 'bg-transparent' : 'bg-gray-50'}`}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: color }}></div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${isEmbedded ? 'bg-transparent' : 'bg-gray-50'} py-8 px-4`}>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        {!isEmbedded && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 mb-6">
            <div className="flex items-center gap-4 mb-4">
              {logo && (
                <img src={logo} alt={name} className="h-16 w-auto" />
              )}
              <div>
                <h1 className="text-3xl font-bold text-gray-900">{name}</h1>
                <p className="text-gray-600">Join Our Team</p>
              </div>
            </div>
            <p className="text-gray-700">
              Explore exciting career opportunities and become part of our growing team.
            </p>
          </div>
        )}

        {/* Search and Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Search */}
            <div className="lg:col-span-2">
              <input
                type="text"
                placeholder="Search jobs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                style={{ focusRing: color } as any}
              />
            </div>

            {/* Department Filter */}
            <select
              value={selectedDepartment}
              onChange={(e) => setSelectedDepartment(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
            >
              {departments.map(dept => (
                <option key={dept} value={dept}>
                  {dept === 'all' ? 'All Departments' : dept}
                </option>
              ))}
            </select>

            {/* Type Filter */}
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
            >
              {types.map(type => (
                <option key={type} value={type}>
                  {type === 'all' ? 'All Types' : type}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Jobs List */}
        <div className="space-y-4">
          {filteredJobs.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
              <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <p className="text-gray-500 text-lg font-medium mb-2">No positions available at the moment</p>
              <p className="text-gray-400 text-sm">Check back soon for new opportunities!</p>
              {!isEmbedded && jobs.length === 0 && (
                <p className="text-gray-400 text-xs mt-4">
                  💡 Tip: Create jobs in the recruitment portal and enable "Post to Career Site"
                </p>
              )}
            </div>
          ) : (
            filteredJobs.map((job) => (
              <div
                key={job.id}
                onClick={() => handleJobClick(job.id)}
                className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow cursor-pointer group"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 
                      className="text-xl font-semibold text-gray-900 group-hover:text-blue-600 transition-colors mb-2"
                      style={{ color: `var(--hover-color, ${color})` } as any}
                    >
                      {job.title}
                    </h3>
                    
                    <div className="flex flex-wrap gap-3 mb-3">
                      {job.department && (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-700">
                          🏢 {job.department}
                        </span>
                      )}
                      {job.location && (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-700">
                          📍 {job.location}
                        </span>
                      )}
                      {job.remote_type && (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-700">
                          💻 {job.remote_type}
                        </span>
                      )}
                      {job.employment_type && (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-green-100 text-green-700">
                          ⏰ {job.employment_type}
                        </span>
                      )}
                    </div>

                    {job.description && (
                      <p className="text-gray-600 line-clamp-2 mb-3">
                        {job.description}
                      </p>
                    )}

                    {(job.salary_min || job.salary_max) && (
                      <p className="text-gray-700 font-medium">
                        💰 {job.salary_currency || '£'}
                        {job.salary_min?.toLocaleString()} - {job.salary_max?.toLocaleString()}
                      </p>
                    )}
                  </div>

                  <button
                    className="ml-4 px-6 py-2 rounded-lg text-white font-medium transition-colors"
                    style={{ backgroundColor: color }}
                  >
                    Apply →
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        {!isEmbedded && (
          <div className="mt-8 text-center text-gray-500 text-sm">
            <p>Powered by Explanatum Recruitment</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PublicJobBoard;
